import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Space,
  Typography,
  Select,
  message,
  Tag,
  Modal,
  Tooltip,
  Row,
  Col,
  Divider,
  Table,
  Form,
  Switch,
  Empty,
  Badge,
  Alert,
  Tabs,
} from "antd";
import {
  CopyOutlined,
  SyncOutlined,
  ApiOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { useGlobal } from "../contexts/GlobalContext";
import type { ProjectItem } from "../contexts/GlobalContext";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/light";
import js from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import bash from "react-syntax-highlighter/dist/esm/languages/hljs/bash";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import request from "../utils/request";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

// API Key 数据类型
interface ApiKeyItem {
  id: number;
  api_key: string;
  name: string;
  prompt?: string;
  enabled: boolean;
  created_at: string;
}

interface ProjectApiKeyValue {
  id: number;
  key: string;
  name: string;
  created_at: string;
}

interface PublicModel {
  id: number;
  modelName: string;
  displayName?: string;
  modelType?: string;
  isDefault?: boolean;
}

// 注册语言
SyntaxHighlighter.registerLanguage("javascript", js);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("python", python);

const ChatAPI: React.FC = () => {
  const { projects, API_BASE_URL, refreshProjects, token } = useGlobal();
  const [resettingKey, setResettingKey] = useState(false);
  const [copyModelName, setCopyModelName] = useState("");
  const [copied, setCopied] = useState(false);
  const [models, setModels] = useState<PublicModel[]>([]);

  const postChatProjects = useMemo(
    () => projects.filter((project) => project.is_postchat),
    [projects]
  );

  // 子 API Key 相关状态
  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKeyItem | null>(null);
  const [form] = Form.useForm();

  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(
    projects.find((project) => project.is_postchat) || null
  );

  const defaultModelName =
    models.find((model) => model.isDefault)?.modelName ||
    models[0]?.modelName ||
    "tianli";
  const apiKeyForExample = selectedProject?.api_key || "YOUR_API_KEY";
  const apiExamples = useMemo(
    () => [
      {
        key: "curl",
        label: "cURL",
        language: "bash",
        code: `curl -X POST https://api.ai.zhheo.com/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKeyForExample}" \\
  -d '{
  "model": "${defaultModelName}",
  "messages": [
    {"role": "system", "content": "你是项目默认 RAG Workflow。"},
    {"role": "user", "content": "请结合项目知识回答这个问题。"}
  ]
}'`,
      },
      {
        key: "javascript",
        label: "JavaScript",
        language: "javascript",
        code: `const response = await fetch("https://api.ai.zhheo.com/v1/chat/completions", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: "Bearer ${apiKeyForExample}",
  },
  body: JSON.stringify({
    model: "${defaultModelName}",
    messages: [
      { role: "system", content: "你是项目默认 RAG Workflow。" },
      { role: "user", content: "请结合项目知识回答这个问题。" },
    ],
  }),
});

const data = await response.json();
console.log(data.choices?.[0]?.message?.content);`,
      },
      {
        key: "python",
        label: "Python",
        language: "python",
        code: `from openai import OpenAI

client = OpenAI(
    api_key="${apiKeyForExample}",
    base_url="https://api.ai.zhheo.com/v1",
)

response = client.chat.completions.create(
    model="${defaultModelName}",
    messages=[
        {"role": "system", "content": "你是项目默认 RAG Workflow。"},
        {"role": "user", "content": "请结合项目知识回答这个问题。"},
    ],
)

print(response.choices[0].message.content)`,
      },
    ],
    [apiKeyForExample, defaultModelName]
  );

  useEffect(() => {
    if (postChatProjects.length === 0) {
      setSelectedProject(null);
      return;
    }

    setSelectedProject((current) => {
      if (!current) {
        return postChatProjects[0];
      }

      return (
        postChatProjects.find((project) => project.id === current.id) ||
        postChatProjects[0]
      );
    });
  }, [postChatProjects]);

  useEffect(() => {
    if (!token) {
      return;
    }

    request
      .get(`${API_BASE_URL}/api/v3/models`)
      .then((response) => {
        if (response.data.code === 200) {
          setModels(response.data.data || []);
        }
      })
      .catch((error) => {
        console.error("获取模型列表失败:", error);
      });
  }, [API_BASE_URL, token]);

  // 获取项目的子 API Keys
  const fetchApiKeys = React.useCallback(
    async (projectId: number) => {
      setLoadingKeys(true);
      try {
        const [keysResponse, projectsResponse] = await Promise.all([
          request.get(`${API_BASE_URL}/api/v3/projects/${projectId}/api-keys`),
          request.get(`${API_BASE_URL}/api/v3/projects`),
        ]);

        if (keysResponse.data.code !== 200) {
          message.error(keysResponse.data.msg || "获取API Key列表失败");
          return;
        }

        if (projectsResponse.data.code !== 200) {
          message.error(projectsResponse.data.msg || "获取项目列表失败");
          return;
        }

        const currentProject = (projectsResponse.data.data?.list || []).find(
          (project: { id: number; api_keys?: ProjectApiKeyValue[] }) =>
            project.id === projectId
        );
        const keyValueMap = new Map<number, ProjectApiKeyValue>(
          (currentProject?.api_keys || []).map((item: ProjectApiKeyValue) => [
            item.id,
            item,
          ])
        );

        setApiKeys(
          (keysResponse.data.data || []).map(
            (item: {
              id: number;
              name: string;
              prompt?: string;
              enabled: boolean;
              created_at: string;
            }) => ({
              id: item.id,
              api_key: keyValueMap.get(item.id)?.key || "",
              name: item.name || keyValueMap.get(item.id)?.name || "",
              prompt: item.prompt || "",
              enabled: item.enabled,
              created_at:
                item.created_at || keyValueMap.get(item.id)?.created_at || "",
            })
          )
        );
      } catch (error: any) {
        message.error(error.response?.data?.msg || "获取API Key列表失败");
      } finally {
        setLoadingKeys(false);
      }
    },
    [API_BASE_URL]
  );

  // 当选择项目改变时,获取对应的API Keys
  useEffect(() => {
    if (selectedProject?.id) {
      fetchApiKeys(selectedProject.id);
    }
  }, [selectedProject?.id, fetchApiKeys]);

  // 创建新的 API Key
  const handleCreateApiKey = async (values: {
    name: string;
    prompt?: string;
  }) => {
    if (!selectedProject) return;

    if (apiKeys.length >= 5) {
      message.warning("每个项目最多支持5个子API Key");
      return;
    }

    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/projects/${selectedProject.id}/api-keys`,
        {
          name: values.name,
          prompt: values.prompt || "",
        }
      );

      if (response.data.code === 200) {
        message.success("API Key创建成功");
        setIsCreateModalVisible(false);
        form.resetFields();
        fetchApiKeys(selectedProject.id);
      } else {
        message.error(response.data.msg || "创建失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "创建失败,请稍后重试");
    }
  };

  // 更新 API Key
  const handleUpdateApiKey = async (values: {
    name: string;
    prompt?: string;
  }) => {
    if (!editingKey) return;

    try {
      const response = await request.put(
        `${API_BASE_URL}/api/v3/projects/${selectedProject?.id}/api-keys/${editingKey.id}`,
        {
          name: values.name,
          prompt: values.prompt || "",
        }
      );

      if (response.data.code === 200) {
        message.success("API Key更新成功");
        setIsEditModalVisible(false);
        setEditingKey(null);
        form.resetFields();
        if (selectedProject?.id) {
          fetchApiKeys(selectedProject.id);
        }
      } else {
        message.error(response.data.msg || "更新失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "更新失败,请稍后重试");
    }
  };

  // 切换 API Key 启用状态
  const handleToggleApiKey = async (id: number, enabled: boolean) => {
    if (!selectedProject) return;

    try {
      const response = await request.patch(
        `${API_BASE_URL}/api/v3/projects/${selectedProject.id}/api-keys/${id}/toggle`,
        {
          enabled,
        }
      );

      if (response.data.code === 200) {
        message.success(enabled ? "已启用" : "已禁用");
        if (selectedProject?.id) {
          fetchApiKeys(selectedProject.id);
        }
      } else {
        message.error(response.data.msg || "操作失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "操作失败,请稍后重试");
    }
  };

  // 删除 API Key
  const handleDeleteApiKey = (id: number, name: string) => {
    Modal.confirm({
      title: "确认删除",
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <Paragraph>
            您确定要删除 API Key <Text strong>"{name}"</Text> 吗?
          </Paragraph>
          <Paragraph type="danger">
            <Text strong>警告:</Text>
            删除后无法恢复,所有使用该密钥的应用将无法访问!
          </Paragraph>
        </>
      ),
      okText: "确认删除",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!selectedProject) return;

        try {
          const response = await request.delete(
            `${API_BASE_URL}/api/v3/projects/${selectedProject.id}/api-keys/${id}`
          );

          if (response.data.code === 200) {
            message.success("删除成功");
            if (selectedProject?.id) {
              fetchApiKeys(selectedProject.id);
            }
          } else {
            message.error(response.data.msg || "删除失败");
          }
        } catch (error: any) {
          message.error(error.response?.data?.msg || "删除失败,请稍后重试");
        }
      },
    });
  };

  // 打开编辑弹窗
  const handleOpenEditModal = (apiKey: ApiKeyItem) => {
    setEditingKey(apiKey);
    form.setFieldsValue({
      name: apiKey.name,
      prompt: apiKey.prompt || "",
    });
    setIsEditModalVisible(true);
  };

  const handleProjectChange = (projectId: number) => {
    const project = postChatProjects.find((p) => p.id === projectId);
    if (project) {
      setSelectedProject(project);
    }
  };

  const handleCopyApiKey = () => {
    if (selectedProject?.api_key) {
      navigator.clipboard.writeText(selectedProject.api_key);
      message.success("API密钥已复制到剪贴板");
    }
  };

  const handleCopyApiUrl = () => {
    navigator.clipboard.writeText("https://api.ai.zhheo.com/v1");
    message.success("API地址已复制到剪贴板");
  };

  const handleResetApiKey = () => {
    if (!selectedProject) return;

    Modal.confirm({
      title: "确认重置API密钥",
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <Paragraph>
            您确定要重置 <Text strong>{selectedProject.name}</Text>{" "}
            的API密钥吗？
          </Paragraph>
          <Paragraph type="danger">
            <Text strong>警告：</Text>
            重置后，原有的API密钥将立即失效！所有使用旧密钥的应用将无法访问，需要更新为新的密钥。
          </Paragraph>
        </>
      ),
      okText: "确认重置",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        setResettingKey(true);
        try {
          const response = await request.post(
            `${API_BASE_URL}/api/v3/projects/${selectedProject.id}/reset-key`
          );

          if (response.data.code === 200) {
            message.success("API密钥重置成功");
            const nextApiKey = response.data.data?.api_key;

            if (nextApiKey) {
              setSelectedProject((current) =>
                current
                  ? {
                      ...current,
                      api_key: nextApiKey,
                    }
                  : current
              );
            }

            await refreshProjects();
          } else {
            message.error(response.data.msg || "重置失败");
          }
        } catch (error: any) {
          message.error(error.response?.data?.msg || "重置失败，请稍后重试");
        } finally {
          setResettingKey(false);
        }
      },
    });
  };

  const handleNextChatClick = () => {
    if (!selectedProject?.api_key) {
      message.warning("请先选择一个项目");
      return;
    }
    const settings = {
      key: selectedProject.api_key,
      url: "https://api.ai.zhheo.com/v1",
    };
    const encodedSettings = encodeURIComponent(JSON.stringify(settings));
    window.open(
      `https://app.nextchat.dev/#/?settings=${encodedSettings}`,
      "_blank"
    );
  };

  const handleWechatClick = () => {
    window.open("https://ai.zhheo.com/docs/", "_blank");
  };

  const handleQQClick = () => {
    window.open("https://ai.zhheo.com/docs/", "_blank");
  };

  const handleDocsClick = () => {
    window.open("https://ai.zhheo.com/docs/", "_blank");
  };

  if (projects.length === 0) {
    return (
      <div
        style={{
          background: "#f0f2f5",
          minHeight: "100%",
          padding: "24px",
          borderRadius: 8,
        }}
      >
        <Card
          variant="outlined"
          className="shadow-card"
          style={{ borderRadius: 12 }}
        >
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ApiOutlined
              style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 24 }}
            />
            <Title level={3}>需要创建一个项目才可使用此功能</Title>
            <Paragraph type="secondary" style={{ marginBottom: 32 }}>
              创建项目后，您将获得专属的API密钥，可用于接入各种对话应用
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={() => (window.location.href = "/console")}
              style={{ minWidth: 200 }}
            >
              返回首页创建项目
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        padding: "16px",
        borderRadius: 8,
      }}
    >
      {/* 顶部信息卡片 */}
      <Card
        variant="borderless"
        className="shadow-card"
        style={{
          marginBottom: 16,
          borderRadius: 8,
          background: "linear-gradient(135deg, #1677ff 0%, #0958d9 100%)",
        }}
        styles={{
          body: { padding: "30px" },
        }}
      >
        <Row gutter={24} align="middle">
          <Col xs={24} md={16}>
            <Title
              level={2}
              style={{
                color: "white",
                margin: 0,
                marginBottom: 16,
              }}
            >
              对话API服务
            </Title>
            <Paragraph
              style={{
                color: "rgba(255, 255, 255, 0.85)",
                fontSize: 16,
                marginBottom: 0,
              }}
            >
              使用 OpenAI 兼容接口调用项目默认 RAG Workflow，旧项目 Key 与新 Agent-first 路由保持兼容
            </Paragraph>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: "center" }}>
            <ApiOutlined
              style={{ fontSize: 80, color: "rgba(255, 255, 255, 0.2)" }}
            />
          </Col>
        </Row>
      </Card>

      {/* API配置卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-terminal-box-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            API配置
          </span>
        }
        variant="borderless"
        className="shadow-card"
        style={{ marginBottom: 16, borderRadius: 8 }}
        extra={
          <Tooltip title="查看详细API文档">
            <Button
              type="link"
              icon={
                <i className="ri-question-line" style={{ fontSize: "16px" }} />
              }
              onClick={handleDocsClick}
            >
              API文档
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={5} style={{ display: "flex", alignItems: "center" }}>
              <span>API地址</span>
              <Tooltip title="OpenAI 兼容地址，请将项目 API Key 作为 Bearer Token 使用">
                <InfoCircleOutlined
                  style={{ marginLeft: 8, fontSize: 14, color: "#1677ff" }}
                />
              </Tooltip>
            </Title>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                value="https://api.ai.zhheo.com/v1"
                readOnly
                style={{
                  borderRadius: "6px 0 0 6px",
                  height: 40,
                  fontSize: 15,
                }}
                prefix={
                  <i
                    className="ri-link"
                    style={{ color: "#1677ff", marginRight: 8 }}
                  />
                }
              />
              <Button
                icon={<CopyOutlined />}
                onClick={handleCopyApiUrl}
                style={{ height: 40 }}
                type="primary"
              >
                复制
              </Button>
            </Space.Compact>
          </div>

          <div>
            <Title level={5} style={{ display: "flex", alignItems: "center" }}>
              <span>API密钥</span>
              <Tooltip title="项目主密钥会路由到该项目默认 RAG Workflow，请妥善保管">
                <InfoCircleOutlined
                  style={{ marginLeft: 8, fontSize: 14, color: "#1677ff" }}
                />
              </Tooltip>
            </Title>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  value={selectedProject?.id}
                  style={{ width: "180px", height: 40 }}
                  onChange={handleProjectChange}
                  placeholder="选择项目"
                  optionLabelProp="label"
                  popupMatchSelectWidth={false}
                >
                  {postChatProjects.map((project) => (
                    <Select.Option
                      key={project.id}
                      value={project.id}
                      label={project.name}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <i
                          className="ri-terminal-box-line"
                          style={{ marginRight: 8 }}
                        />
                        {project.name}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
                <Input
                  value={selectedProject?.api_key || ""}
                  readOnly
                  placeholder="请选择项目查看API密钥"
                  style={{
                    flex: 1,
                    height: 40,
                    fontSize: 15,
                    fontFamily: "monospace",
                  }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyApiKey}
                  disabled={!selectedProject?.api_key}
                  style={{ height: 40 }}
                  type="primary"
                >
                  复制
                </Button>
                <Tooltip title="重置密钥后，旧密钥将立即失效">
                  <Button
                    icon={<SyncOutlined spin={resettingKey} />}
                    onClick={handleResetApiKey}
                    disabled={!selectedProject || resettingKey}
                    danger
                    style={{ height: 40 }}
                  >
                    重置
                  </Button>
                </Tooltip>
              </Space.Compact>

              {selectedProject && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#f9f9f9",
                    borderRadius: 8,
                  }}
                >
                  <Space size="middle">
                    <Text type="secondary">项目ID: {selectedProject.id}</Text>
                    <Divider type="vertical" />
                    <Text type="secondary">路由: 默认 RAG Workflow</Text>
                  </Space>
                </div>
              )}
            </Space>
          </div>
        </Space>
      </Card>

      {/* 子 API Key 管理卡片 */}
      {selectedProject && (
        <Card
          title={
            <span>
              <KeyOutlined style={{ marginRight: "8px", color: "#1677ff" }} />
              子 API Key 管理
              <Badge
                count={`${apiKeys.length}/5`}
                style={{
                  marginLeft: 12,
                  backgroundColor: apiKeys.length >= 5 ? "#ff4d4f" : "#52c41a",
                }}
              />
            </span>
          }
          variant="borderless"
          className="shadow-card"
          style={{ marginBottom: 16, borderRadius: 8 }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setIsCreateModalVisible(true);
              }}
              disabled={apiKeys.length >= 5}
            >
              创建子密钥
            </Button>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div
              style={{
                padding: "12px 16px",
                background: "#f0f7ff",
                borderRadius: 8,
                border: "1px solid #d6e8fc",
              }}
            >
              <Text type="secondary">
                <InfoCircleOutlined
                  style={{ marginRight: 8, color: "#1677ff" }}
                />
                子 API Key
                可以设置独立的提示词,便于不同场景使用。使用项目主密钥时提示词保持不变,最多可创建5个子密钥。
              </Text>
            </div>

            {loadingKeys ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <SyncOutlined spin style={{ fontSize: 32, color: "#1677ff" }} />
                <div style={{ marginTop: 16 }}>加载中...</div>
              </div>
            ) : apiKeys.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无子 API Key"
                style={{ padding: "40px 0" }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.resetFields();
                    setIsCreateModalVisible(true);
                  }}
                >
                  创建第一个子密钥
                </Button>
              </Empty>
            ) : (
              <Table
                dataSource={apiKeys}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: "名称",
                    dataIndex: "name",
                    key: "name",
                    width: 180,
                    render: (text: string) => (
                      <Text strong style={{ fontSize: 14 }}>
                        <i
                          className="ri-key-2-line"
                          style={{ marginRight: 6 }}
                        />
                        {text}
                      </Text>
                    ),
                  },
                  {
                    title: "API Key",
                    dataIndex: "api_key",
                    key: "api_key",
                    ellipsis: true,
                    render: (text: string) => (
                      <Space>
                        <Text
                          code
                          style={{
                            fontFamily: "monospace",
                            fontSize: 13,
                          }}
                          copyable={{ text }}
                        >
                          {text}
                        </Text>
                      </Space>
                    ),
                  },
                  {
                    title: "提示词",
                    dataIndex: "prompt",
                    key: "prompt",
                    width: 200,
                    ellipsis: true,
                    render: (text: string) => (
                      <Tooltip title={text || "未设置"}>
                        <Text
                          type="secondary"
                          ellipsis
                          style={{ maxWidth: 180 }}
                        >
                          {text || <Text type="secondary">未设置</Text>}
                        </Text>
                      </Tooltip>
                    ),
                  },
                  {
                    title: "状态",
                    dataIndex: "enabled",
                    key: "enabled",
                    width: 100,
                    align: "center" as const,
                    render: (enabled: boolean, record: ApiKeyItem) => (
                      <Switch
                        checked={enabled}
                        onChange={(checked) =>
                          handleToggleApiKey(record.id, checked)
                        }
                        checkedChildren="启用"
                        unCheckedChildren="禁用"
                      />
                    ),
                  },
                  {
                    title: "创建时间",
                    dataIndex: "created_at",
                    key: "created_at",
                    width: 160,
                    render: (text: string) => (
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {text}
                      </Text>
                    ),
                  },
                  {
                    title: "操作",
                    key: "action",
                    width: 120,
                    align: "center" as const,
                    render: (_: any, record: ApiKeyItem) => (
                      <Space size="small">
                        <Tooltip title="编辑">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenEditModal(record)}
                            size="small"
                          />
                        </Tooltip>
                        <Tooltip title="删除">
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() =>
                              handleDeleteApiKey(record.id, record.name)
                            }
                            size="small"
                          />
                        </Tooltip>
                      </Space>
                    ),
                  },
                ]}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              />
            )}
          </Space>
        </Card>
      )}

      {/* 快速接入卡片 */}
      <Card
        title={
          <span>
            <i
              className="ri-rocket-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            快速接入
          </span>
        }
        variant="borderless"
        className="shadow-card"
        style={{ marginBottom: 16, borderRadius: 8 }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={5}>使用API聊天：</Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={handleNextChatClick}
                  className="platform-card"
                  style={{ textAlign: "center", marginBottom: 16 }}
                >
                  <div
                    style={{ fontSize: 36, color: "#1677ff", marginBottom: 12 }}
                  >
                    <i className="ri-chat-4-line" />
                  </div>
                  <div style={{ fontWeight: 500 }}>NextChat</div>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    开源的聊天客户端
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={handleWechatClick}
                  className="platform-card"
                  style={{ textAlign: "center" }}
                >
                  <div
                    style={{ fontSize: 36, color: "#07C160", marginBottom: 12 }}
                  >
                    <i className="ri-wechat-line" />
                  </div>
                  <div style={{ fontWeight: 500 }}>微信接入</div>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    支持公众号/机器人
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={handleQQClick}
                  className="platform-card"
                  style={{ textAlign: "center" }}
                >
                  <div
                    style={{ fontSize: 36, color: "#12B7F5", marginBottom: 12 }}
                  >
                    <i className="ri-qq-line" />
                  </div>
                  <div style={{ fontWeight: 500 }}>QQ接入</div>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    支持QQ/频道机器人
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={handleDocsClick}
                  className="platform-card"
                  style={{ textAlign: "center" }}
                >
                  <div
                    style={{ fontSize: 36, color: "#722ED1", marginBottom: 12 }}
                  >
                    <i className="ri-book-2-line" />
                  </div>
                  <div style={{ fontWeight: 500 }}>接入文档</div>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    查看完整开发指南
                  </div>
                </Card>
              </Col>
            </Row>
          </div>

          <div>
            <Title level={5} style={{ display: "flex", alignItems: "center" }}>
              <span>可用模型</span>
              <Tooltip title="来自 /api/v3/models 的公开模型列表，请在 OpenAI 兼容请求中使用 modelName">
                <InfoCircleOutlined
                  style={{ marginLeft: 8, fontSize: 14, color: "#1677ff" }}
                />
              </Tooltip>
            </Title>
            {models.length ? (
              <Space wrap>
                {models.map((model) => (
                  <Tag
                    key={model.id || model.modelName}
                    color={model.isDefault ? "success" : "processing"}
                    style={{ padding: "4px 12px", fontSize: 14, cursor: "pointer" }}
                    onClick={() => {
                      navigator.clipboard.writeText(model.modelName);
                      setCopyModelName(model.modelName);
                      message.success(`已复制模型名称: ${model.modelName}`);
                      setTimeout(() => setCopyModelName(""), 3000);
                    }}
                  >
                    <i className="ri-braces-line" style={{ marginRight: 6 }} />
                    {model.displayName || model.modelName}
                    {model.displayName && (
                      <Text type="secondary" style={{ marginLeft: 6 }}>
                        {model.modelName}
                      </Text>
                    )}
                    {model.isDefault && <Text style={{ marginLeft: 6 }}>默认</Text>}
                    {copyModelName === model.modelName && (
                      <CheckOutlined style={{ marginLeft: 6, color: "#52c41a" }} />
                    )}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无公开模型" />
            )}
            <Paragraph type="secondary" style={{ marginTop: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 8 }} />
              项目 API Key 默认进入该项目的默认 RAG Workflow，模型由后端模型中心统一路由和计费。
            </Paragraph>
          </div>

          <div
            style={{
              background: "#f0f7ff",
              padding: 16,
              borderRadius: 8,
              border: "1px solid #d6e8fc",
            }}
          >
            <Title
              level={5}
              style={{
                color: "#1677ff",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
              }}
            >
              <i className="ri-terminal-line" style={{ marginRight: 8 }} />
              API调用示例
            </Title>
            <Tabs
              items={apiExamples.map((example) => ({
                key: example.key,
                label: example.label,
                children: (
                  <div
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: 8,
                      overflow: "hidden",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 16px",
                        background: "#fafafa",
                        borderBottom: "1px solid #d9d9d9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Text strong>{example.label}</Text>
                      <Button
                        type={copied ? "primary" : "text"}
                        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(example.code);
                          setCopied(true);
                          message.success("代码已复制到剪贴板");
                          setTimeout(() => setCopied(false), 2000);
                        }}
                      >
                        {copied ? "已复制" : "复制代码"}
                      </Button>
                    </div>
                    <div
                      style={{
                        maxHeight: "400px",
                        overflow: "auto",
                        background: "#fff",
                      }}
                    >
                      <SyntaxHighlighter
                        language={example.language}
                        style={docco}
                        customStyle={{
                          margin: 0,
                          background: "#fff",
                          padding: "8px 16px",
                        }}
                      >
                        {example.code}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                ),
              }))}
            />
            <div style={{ whiteSpace: "pre-wrap" }}>
              <div
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "8px 16px",
                    background: "#fafafa",
                    borderBottom: "1px solid #d9d9d9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text strong>CURL</Text>
                  <Button
                    type={copied ? "primary" : "text"}
                    icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard
                        .writeText(`curl -X POST https://api.ai.zhheo.com/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${selectedProject?.api_key || "YOUR_API_KEY"}" \\
  -d '{
  "model": "${defaultModelName}",
  "messages": [
    {"role": "system", "content": "你是项目默认 RAG Workflow。"},
    {"role": "user", "content": "请结合项目知识回答这个问题。"}
  ]
}'`);
                      setCopied(true);
                      message.success("代码已复制到剪贴板");
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? "已复制" : "复制代码"}
                  </Button>
                </div>
                <div
                  style={{
                    maxHeight: "400px",
                    overflow: "auto",
                    background: "#fff",
                  }}
                >
                  <SyntaxHighlighter
                    language="javascript"
                    style={docco}
                    customStyle={{
                      margin: 0,
                      background: "#fff",
                      padding: "8px 16px",
                    }}
                  >
                    {`curl -X POST https://api.ai.zhheo.com/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${selectedProject?.api_key || "YOUR_API_KEY"}" \\
  -d '{
  "model": "${defaultModelName}",
  "messages": [
    {"role": "system", "content": "你是项目默认 RAG Workflow。"},
    {"role": "user", "content": "请结合项目知识回答这个问题。"}
  ]
}'`}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          </div>
        </Space>
      </Card>

      {/* 创建子 API Key Modal */}
      <Modal
        title={
          <span>
            <PlusOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            创建子 API Key
          </span>
        }
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateApiKey}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="密钥名称"
            name="name"
            rules={[
              { required: true, message: "请输入密钥名称" },
              { max: 50, message: "名称不能超过50个字符" },
            ]}
          >
            <Input
              placeholder="例如:客服机器人、知识问答等"
              prefix={<KeyOutlined style={{ color: "#1677ff" }} />}
              maxLength={50}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span>
                  提示词设置
                  <Tooltip title="设置此API Key专属的提示词,不设置则使用默认提示词。提示词用于定义AI的角色和回答方式。">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, color: "#1677ff" }}
                    />
                  </Tooltip>
                </span>
                <Space size="small">
                  <Tooltip title="点击插入问题变量,将被替换为用户的实际问题">
                    <Button
                      size="small"
                      type="text"
                      style={{
                        fontSize: 12,
                        padding: "0 8px",
                        height: 24,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        color: "#722ed1",
                        fontFamily: "monospace",
                      }}
                      onClick={() => {
                        const textarea = document.querySelector(
                          'textarea[id="prompt"]'
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentValue =
                            form.getFieldValue("prompt") || "";
                          const newValue =
                            currentValue.substring(0, start) +
                            "{{question}}" +
                            currentValue.substring(end);
                          form.setFieldsValue({ prompt: newValue });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 12, start + 12);
                          }, 0);
                        }
                      }}
                    >
                      <i
                        className="ri-question-line"
                        style={{ marginRight: 4 }}
                      />
                      {"{{question}}"}
                    </Button>
                  </Tooltip>
                  <Tooltip title="点击插入答案变量,将被替换为知识库检索到的相关信息">
                    <Button
                      size="small"
                      type="text"
                      style={{
                        fontSize: 12,
                        padding: "0 8px",
                        height: 24,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        color: "#52c41a",
                        fontFamily: "monospace",
                      }}
                      onClick={() => {
                        const textarea = document.querySelector(
                          'textarea[id="prompt"]'
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentValue =
                            form.getFieldValue("prompt") || "";
                          const newValue =
                            currentValue.substring(0, start) +
                            "{{answer}}" +
                            currentValue.substring(end);
                          form.setFieldsValue({ prompt: newValue });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 10, start + 10);
                          }, 0);
                        }
                      }}
                    >
                      <i
                        className="ri-database-2-line"
                        style={{ marginRight: 4 }}
                      />
                      {"{{answer}}"}
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            }
            name="prompt"
          >
            <TextArea
              id="prompt"
              placeholder={`请输入提示词模板，例如:\n\n你是一个专业的客服机器人。\n\n用户问题: {{question}}\n知识库信息: {{answer}}\n\n请根据以上信息，用友好专业的语气回答用户的问题。`}
              rows={8}
              maxLength={2000}
              showCount
              style={{ fontFamily: "inherit" }}
            />
          </Form.Item>

          <Alert
            message="变量说明"
            description={
              <div style={{ fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}>
                  <Tag
                    color="purple"
                    style={{ fontFamily: "monospace", marginRight: 8 }}
                  >
                    {"{{question}}"}
                  </Tag>
                  <span>用户提出的问题,在对话时自动替换</span>
                </div>
                <div>
                  <Tag
                    color="green"
                    style={{ fontFamily: "monospace", marginRight: 8 }}
                  >
                    {"{{answer}}"}
                  </Tag>
                  <span>知识库检索到的相关信息,自动填充到提示词中</span>
                </div>
              </div>
            }
            type="info"
            showIcon
            icon={<i className="ri-lightbulb-line" />}
            style={{ marginTop: 8 }}
          />
        </Form>
      </Modal>

      {/* 编辑子 API Key Modal */}
      <Modal
        title={
          <span>
            <EditOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            编辑子 API Key
          </span>
        }
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingKey(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="保存"
        cancelText="取消"
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateApiKey}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="密钥名称"
            name="name"
            rules={[
              { required: true, message: "请输入密钥名称" },
              { max: 50, message: "名称不能超过50个字符" },
            ]}
          >
            <Input
              placeholder="例如:客服机器人、知识问答等"
              prefix={<KeyOutlined style={{ color: "#1677ff" }} />}
              maxLength={50}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span>
                  提示词设置
                  <Tooltip title="设置此API Key专属的提示词,不设置则使用默认提示词。提示词用于定义AI的角色和回答方式。">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, color: "#1677ff" }}
                    />
                  </Tooltip>
                </span>
                <Space size="small">
                  <Tooltip title="点击插入问题变量,将被替换为用户的实际问题">
                    <Button
                      size="small"
                      type="text"
                      style={{
                        fontSize: 12,
                        padding: "0 8px",
                        height: 24,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        color: "#722ed1",
                        fontFamily: "monospace",
                      }}
                      onClick={() => {
                        const textarea = document.querySelector(
                          'textarea[id="prompt-edit"]'
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentValue =
                            form.getFieldValue("prompt") || "";
                          const newValue =
                            currentValue.substring(0, start) +
                            "{{question}}" +
                            currentValue.substring(end);
                          form.setFieldsValue({ prompt: newValue });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 12, start + 12);
                          }, 0);
                        }
                      }}
                    >
                      <i
                        className="ri-question-line"
                        style={{ marginRight: 4 }}
                      />
                      {"{{question}}"}
                    </Button>
                  </Tooltip>
                  <Tooltip title="点击插入答案变量,将被替换为知识库检索到的相关信息">
                    <Button
                      size="small"
                      type="text"
                      style={{
                        fontSize: 12,
                        padding: "0 8px",
                        height: 24,
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        color: "#52c41a",
                        fontFamily: "monospace",
                      }}
                      onClick={() => {
                        const textarea = document.querySelector(
                          'textarea[id="prompt-edit"]'
                        ) as HTMLTextAreaElement;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const currentValue =
                            form.getFieldValue("prompt") || "";
                          const newValue =
                            currentValue.substring(0, start) +
                            "{{answer}}" +
                            currentValue.substring(end);
                          form.setFieldsValue({ prompt: newValue });
                          setTimeout(() => {
                            textarea.focus();
                            textarea.setSelectionRange(start + 10, start + 10);
                          }, 0);
                        }
                      }}
                    >
                      <i
                        className="ri-database-2-line"
                        style={{ marginRight: 4 }}
                      />
                      {"{{answer}}"}
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            }
            name="prompt"
          >
            <TextArea
              id="prompt-edit"
              placeholder={`请输入提示词模板，例如:\n\n你是一个专业的客服机器人。\n\n用户问题: {{question}}\n知识库信息: {{answer}}\n\n请根据以上信息，用友好专业的语气回答用户的问题。`}
              rows={8}
              maxLength={2000}
              showCount
              style={{ fontFamily: "inherit" }}
            />
          </Form.Item>

          <Alert
            message="变量说明"
            description={
              <div style={{ fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}>
                  <Tag
                    color="purple"
                    style={{ fontFamily: "monospace", marginRight: 8 }}
                  >
                    {"{{question}}"}
                  </Tag>
                  <span>用户提出的问题,在对话时自动替换</span>
                </div>
                <div>
                  <Tag
                    color="green"
                    style={{ fontFamily: "monospace", marginRight: 8 }}
                  >
                    {"{{answer}}"}
                  </Tag>
                  <span>知识库检索到的相关信息,自动填充到提示词中</span>
                </div>
              </div>
            }
            type="info"
            showIcon
            icon={<i className="ri-lightbulb-line" />}
            style={{ marginTop: 8 }}
          />
        </Form>
      </Modal>

      <style>{`
        .shadow-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .platform-card {
          transition: all 0.3s ease;
        }

        .platform-card:hover {
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default ChatAPI;
